import { useQuery } from '@tanstack/react-query';
import { backendServices } from 'common';
import {
    buildSimCustomerNumber,
    buildSimCustomerViewModel,
    findExistingSimCustomer,
} from './_shared/simCustomerNumber';
import { findScheduleForClass } from './simSchedules';
import { SIM_DOCUMENT_CLASS, SimDocumentClass } from './_shared/simDocumentVM';
import { SupplierPendingSimRequestFlags } from './useSupplierPendingSimRequests';

export interface SimCustomerIds {
    taxDetails: number | null;
    remittance: number | null;
    certificateOfInsurance: number | null;
}

export interface SimCustomerNumbers {
    taxDetails: string | null;
    remittance: string | null;
    certificateOfInsurance: string | null;
}

/** Per-questionnaire `Customer.ID`s keyed by `BusinessDocSubTypeId`. */
export type QuestionnaireCustomerIds = Record<number, number>;

/** Per-questionnaire `Customer.CustomerNumber`s keyed by `BusinessDocSubTypeId`. */
export type QuestionnaireCustomerNumbers = Record<number, string>;

const EMPTY_IDS: SimCustomerIds = {
    taxDetails: null,
    remittance: null,
    certificateOfInsurance: null,
};

const EMPTY_NUMBERS: SimCustomerNumbers = {
    taxDetails: null,
    remittance: null,
    certificateOfInsurance: null,
};

interface EnsuredSimCustomers {
    customerIds: SimCustomerIds;
    customerNumbers: SimCustomerNumbers;
    questionnaireCustomerIds: QuestionnaireCustomerIds;
    questionnaireCustomerNumbers: QuestionnaireCustomerNumbers;
}

const FIELD_FOR_CLASS: Record<SimDocumentClass, keyof SimCustomerIds> = {
    [SIM_DOCUMENT_CLASS.W9]: 'taxDetails',
    [SIM_DOCUMENT_CLASS.Remittance]: 'remittance',
    [SIM_DOCUMENT_CLASS.InsuranceCertificate]: 'certificateOfInsurance',
};

function enabledClasses(flags: SupplierPendingSimRequestFlags): SimDocumentClass[] {
    const out: SimDocumentClass[] = [];
    if (flags.taxDetails) {
        out.push(SIM_DOCUMENT_CLASS.W9);
    }
    if (flags.remittance) {
        out.push(SIM_DOCUMENT_CLASS.Remittance);
    }
    if (flags.certificateOfInsurance) {
        out.push(SIM_DOCUMENT_CLASS.InsuranceCertificate);
    }
    return out;
}

function flagsKey(flags: SupplierPendingSimRequestFlags): string {
    return `${flags.taxDetails ? 1 : 0}${flags.remittance ? 1 : 0}${flags.certificateOfInsurance ? 1 : 0}`;
}

function escapeOData(value: string): string {
    return value.replace(/'/g, "''");
}

/**
 * TEMPORARY: find-or-create the per-supplier `dbo.Customer` row for each
 * enabled SIM class so post-signup submissions resolve to the right
 * procurement site. Mirrors `SupplierSIMRequestCreation.cs:191-224` so
 * rows here match the daily batch's. Gated by CSP EnableClientSideCustomerCreation;
 * delete every site checking that flag once backend Customer auto-creation lands.
 */
export function useEnsureSimCustomers(args: {
    supplierCompanyId: number | null;
    buyerCompanyId: number | null;
    flags: SupplierPendingSimRequestFlags;
    schedules: backendServices.ViewModels.SIMDocumentTypeScheduleViewModel[];
    /** `BusinessDocSubTypeId`s of the buyer's scheduled questionnaires. */
    questionnaireClassIds: number[];
    /** CSP FeaturesEnabled: EnableClientSideCustomerCreation */
    clientSideCustomerCreationEnabled?: boolean;
    enabled?: boolean;
}) {
    const {
        supplierCompanyId,
        buyerCompanyId,
        flags,
        schedules,
        questionnaireClassIds,
        clientSideCustomerCreationEnabled = false,
        enabled = true,
    } = args;

    const isEnabled =
        clientSideCustomerCreationEnabled &&
        enabled &&
        supplierCompanyId != null &&
        buyerCompanyId != null &&
        (flags.taxDetails || flags.remittance || flags.certificateOfInsurance || questionnaireClassIds.length > 0);

    const questionnaireClassKey = [...questionnaireClassIds].sort((a, b) => a - b).join(',');

    const query = useQuery(
        ['EnsureSimCustomers', supplierCompanyId, buyerCompanyId, flagsKey(flags), questionnaireClassKey],
        async (): Promise<EnsuredSimCustomers> => {
            const companyApi = new backendServices.Apis.CompanyApi();
            const profileApi = new backendServices.Apis.ProfileApi();

            const [buyerRes, buyerDestProfilesRes] = await Promise.all([
                companyApi.getCompany(buyerCompanyId!),
                profileApi.getProfileSearch({
                    profileType: backendServices.ViewModels.ProfileType.BuyerDestination,
                    companyId: buyerCompanyId!,
                }),
            ]);

            const buyer = buyerRes.data[0];
            if (!buyer || buyer.TransceptaCustomerID == null) {
                throw new Error('Buyer is missing TransceptaCustomerID; cannot derive SIM CustomerNumber.');
            }
            const buyerTransceptaCustomerId = buyer.TransceptaCustomerID;
            const buyerDestinationProfileId = buyerDestProfilesRes.data[0]?.Id;
            if (buyerDestinationProfileId == null) {
                throw new Error('Buyer has no BuyerDestination profile; cannot create SIM customers.');
            }

            // Static classes map onto SimCustomerIds fields; questionnaire classes onto their subTypeId.
            const targetClasses: { docClass: number; field: keyof SimCustomerIds | null }[] = [
                ...enabledClasses(flags).map((docClass) => ({ docClass, field: FIELD_FOR_CLASS[docClass] })),
                ...questionnaireClassIds.map((docClass) => ({ docClass, field: null })),
            ];
            const expectedNumbers = targetClasses.map(({ docClass, field }) => {
                const schedule = findScheduleForClass(schedules, docClass);
                if (!schedule) {
                    throw new Error(`No SIM schedule found for document class ${docClass}.`);
                }
                return {
                    docClass,
                    field,
                    schedule,
                    expected: buildSimCustomerNumber({
                        buyerTransceptaCustomerId,
                        simDocumentTypeDescription: schedule.Description,
                    }),
                };
            });

            // Default page size (15) would miss matches if the supplier has many existing customers.
            const filter = expectedNumbers
                .map(({ expected }) => `CustomerNumber eq '${escapeOData(expected)}'`)
                .join(' or ');
            const customersRes = await companyApi.getCustomerLocations({
                companyId: supplierCompanyId!,
                $filter: filter,
                $top: expectedNumbers.length,
            });
            const existing = customersRes.data.Items ?? [];
            const result: EnsuredSimCustomers = {
                customerIds: { ...EMPTY_IDS },
                customerNumbers: { ...EMPTY_NUMBERS },
                questionnaireCustomerIds: {},
                questionnaireCustomerNumbers: {},
            };

            for (const { docClass, field, schedule, expected } of expectedNumbers) {
                const match = findExistingSimCustomer(existing, expected);
                let customerId: number;
                if (match?.ID != null) {
                    customerId = match.ID;
                } else {
                    const body = buildSimCustomerViewModel({
                        supplierCompanyId: supplierCompanyId!,
                        schedule,
                        buyer: {
                            Name: buyer.Name,
                            Address1: buyer.Address1,
                            TransceptaCustomerID: buyerTransceptaCustomerId,
                        },
                        buyerDestinationProfileId,
                    });
                    const createRes = await companyApi.postCustomer(body);
                    if (createRes.data?.ID == null) {
                        throw new Error(`POST /Company/TradingPartners/Customer returned no ID for ${expected}.`);
                    }
                    customerId = createRes.data.ID;
                }

                if (field != null) {
                    result.customerIds[field] = customerId;
                    result.customerNumbers[field] = expected;
                } else {
                    result.questionnaireCustomerIds[docClass] = customerId;
                    result.questionnaireCustomerNumbers[docClass] = expected;
                }
            }

            return result;
        },
        {
            enabled: isEnabled,
            staleTime: Infinity,
            cacheTime: Infinity,
            retry: false,
        }
    );

    return {
        customerIds: query.data?.customerIds ?? null,
        customerNumbers: query.data?.customerNumbers ?? null,
        questionnaireCustomerIds: query.data?.questionnaireCustomerIds ?? null,
        questionnaireCustomerNumbers: query.data?.questionnaireCustomerNumbers ?? null,
        isLoading: isEnabled ? query.isLoading || query.isFetching : false,
        isError: query.isError,
        error: query.error,
    };
}
